import CuideiLanding from 'modules/cuidei/CuideiLanding'
import { HOME_GEO } from 'modules/landings/geo/content/home'
import GeoPage from 'modules/landings/geo/GeoPage'
import IndexPage from 'modules/landings/IndexPage'
import IshaLandingPage from 'modules/landings/IshaLandingPage'
import MedLeveHomePage from 'modules/landings/MedLeveHome'
import { MEDLEVE_ORGANIC_HOME_LIVE } from 'modules/landings/MedLeveHome/constants'
import MedLevePage from 'modules/landings/MedLevePage'
import WeightLandingPage from 'modules/landings/WeightLandingPage'
import { isOmensHost } from 'modules/omensSeoTechnical'
import { GetServerSideProps, NextPage } from 'next'
import { FC, useEffect, useState } from 'react'
import { getCookies, setCookies } from 'utils/cookieHelpers'

interface IndexPageProps {
  shouldRedirect: string
  favicon: string
  /**
   * Set ONLY for the MedLeve host once the organic homepage is live (`MEDLEVE_ORGANIC_HOME_LIVE`): flags the
   * page as a MedLeve organic page so `_app` can server-render it (no client-only gate). The key is absent
   * otherwise and for every other brand, so their page props stay exactly as before.
   */
  medleveOrganic?: true
  /** omens.com.br: the home gets the Omens SEO tags (OMENS_PAGES `hostScoped`). */
  omensHost: boolean
  /** omens.com.br: the Omens home renders on the server, outside the client-only PersistGate (see _app). */
  ssrContent?: true
}

export const getServerSideProps: GetServerSideProps<IndexPageProps> = async (context) => {
  const { req } = context
  const hostname = req.headers.host || ''
  let shouldRedirect
  let favicon

  // Cuidei hosts normally never get here: next.config rewrites their '/' to the conditions hub (/cuidei-hub).
  if (hostname.includes('cuidei')) {
    // Cuidei brand by subdomain/host: cuidei.localhost, cuidei.com.br, www.cuidei.com.br
    shouldRedirect = 'cuidei'
    favicon = '/favicon.png'
  } else if (hostname.includes('omens.fit')) {
    shouldRedirect = 'men'
    favicon = '/favicon.png'
  } else if (hostname.includes('souisha.')) {
    shouldRedirect = 'women'
    favicon = '/favicon-isha.png'
  } else if (hostname.includes('medleve.com') || hostname.includes('medleve.localhost')) {
    shouldRedirect = 'medleve'
    favicon = '/medleve-favicon.png'
  } else if (hostname.includes('medleve')) {
    shouldRedirect = 'medleve'
    favicon = '/medleve-favicon.png'
  } else {
    shouldRedirect = 'index'
    favicon = '/favicon.png'
  }

  return {
    props: {
      shouldRedirect,
      favicon,
      omensHost: isOmensHost(hostname),
      ...(shouldRedirect === 'index' && isOmensHost(hostname) && { ssrContent: true as const }),
      ...(shouldRedirect === 'medleve' && MEDLEVE_ORGANIC_HOME_LIVE && { medleveOrganic: true as const })
    }
  }
}

const Index: NextPage<IndexPageProps> = ({ shouldRedirect, medleveOrganic, ssrContent }) => {
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const weightCookie = getCookies('isWeightAddress')
    const ishaCookie = getCookies('isIshaAddress')
    const medleveCookie = getCookies('isMedleveAddress')

    if (shouldRedirect === 'men' && !weightCookie.isValid) {
      setCookies({ name: 'isWeightAddress', value: 'true' })
    }
    if (shouldRedirect === 'women' && !ishaCookie.isValid) {
      setCookies({ name: 'isIshaAddress', value: 'true' })
    }
    if (shouldRedirect === 'medleve' && !medleveCookie.isValid) {
      setCookies({ name: 'isMedleveAddress', value: 'true' })
    }
    setLoading(false)
  }, [shouldRedirect])

  // MedLeve organic homepage (only once live): rendered on the server and on the first client render (no
  // `loading` gate), so its content is in the server HTML. The brand cookie is still set by the effect above.
  if (medleveOrganic) return <MedLeveHomePage />
  // Omens home on omens.com.br: same, its content is in the server HTML (no brand cookie to set).
  if (ssrContent) return <OmensHome />

  if (loading) return null
  if (shouldRedirect == 'cuidei') {
    // CuideiLanding captures pharmacy attribution + sets the brand cookie on mount.
    return <CuideiLanding />
  } else if (shouldRedirect == 'men') {
    return <WeightLandingPage />
  } else if (shouldRedirect == 'women') {
    return <IshaLandingPage />
  } else if (shouldRedirect == 'medleve') {
    return <MedLevePage />
  } else {
    return <OmensHome />
  }
}

/**
 * The GEO home (PRD section 7), promoted from /home-v2 on 28/10/2026 (the copy answers a 301, next.config.js).
 * Validated without the Ads vocabulary switch of the shared sections (a Lote 1 copy): kept as validated.
 */
const OmensHome: FC = () => (
  <GeoPage adsSafe={false}>
    <IndexPage geo={HOME_GEO} />
  </GeoPage>
)

export default Index
